// Le parcours « Vous postulez ? » en quatre étapes (lot 3 de la refonte,
// maquette « Fiche »). Il remplace six boutons de même poids par un ordre :
// le CV, la lettre (facultative), postuler chez l'employeur, « c'est envoyé ? ».
//
// RIEN N'EST STOCKÉ DEUX FOIS. Chaque étape se lit dans ce qui existe déjà : le
// dossier (étape 1), sa lettre (2), la candidature et son statut (3 et 4).
// Seul « je n'ai pas besoin de lettre » n'a pas de trace en base : c'est un
// confort d'écran, gardé dans le navigateur.

import { useState } from 'react'
import { Check, ExternalLink } from 'lucide-react'
import { get, post, type CandidatureStatus } from '../lib/api'
import { useT } from '../lib/i18n'
import { fullDate, parseDate } from '../lib/format'
import { JOURS_AVANT_RELANCE } from '../lib/prochaineEtape'
import { useData, ActionButton, useToast } from './common'

const cleSansLettre = (dossierId: number) => `radar.sansLettre.${dossierId}`
function lireSansLettre(dossierId: number): boolean {
  try {
    return localStorage.getItem(cleSansLettre(dossierId)) === '1'
  } catch {
    return false
  }
}

export function ParcoursCandidature({
  offreId,
  dossierId,
  consultantId,
  employeur,
  urlOffre,
  score,
  scoreEstime,
  candidature,
  onRedigerLettre,
  rafraichir,
  carteCv,
  onChanged,
}: {
  offreId: number
  dossierId: number
  consultantId: number
  /** Nul quand l'offre ne nomme pas son employeur (extension, agrégateur) :
   *  le nom du site (« Extension Chrome ») n'en tient jamais lieu. */
  employeur: string | null
  urlOffre: string | null
  score: number
  scoreEstime: number
  candidature: { id: number; status: CandidatureStatus } | undefined
  /** « Rédiger la lettre » : l'hôte ouvre l'onglet Lettre de l'atelier, qui
   *  pose les questions d'angle. Le parcours n'écrit plus la lettre lui-même :
   *  il le faisait sans ces questions (issue #178). */
  onRedigerLettre: () => void
  /** Change quand l'atelier vient d'écrire une lettre : l'étape 2 se relit. */
  rafraichir?: number
  /** La carte « Votre CV » (aperçu réduit, ouvrir, télécharger), posée dans
   *  l'étape 1 : le document lui-même s'ouvre en fenêtre. */
  carteCv?: React.ReactNode
  onChanged: () => void
}) {
  const t = useT()
  const toast = useToast()
  const dossier = useData(() => get<{ letter: unknown | null }>(`/api/dossiers/${dossierId}`), [dossierId, rafraichir])
  const envoi = useData(
    () => (candidature ? get<{ depuis: string | null }>(`/api/candidatures/${candidature.id}/relance`) : Promise.resolve(null)),
    [candidature?.id, candidature?.status]
  )
  const [sansLettre, setSansLettre] = useState(() => lireSansLettre(dossierId))
  const [pageOuverte, setPageOuverte] = useState(false)

  const lettre = Boolean(dossier.data?.letter) || sansLettre
  const statut = candidature?.status
  const envoyee = statut !== undefined && statut !== 'a_envoyer'
  const postule = envoyee || pageOuverte
  const etape = !lettre ? 2 : !postule ? 3 : !envoyee ? 4 : 5

  const creerCandidature = async (): Promise<number> => {
    if (candidature) return candidature.id
    await post(`/api/offers/${offreId}/candidatures`, { consultantId })
    const k = await get<{ candidatures: { id: number; consultantId: number }[] }>(`/api/offers/${offreId}`)
    return k.candidatures.find((c) => c.consultantId === consultantId)!.id
  }

  const pas = (n: number, titre: string, corps: React.ReactNode) => {
    const etat = n < etape ? 'ok' : n === etape ? 'on' : ''
    return (
      <li className={`pc-pas ${etat}`} key={n}>
        <span className="pc-num">{etat === 'ok' ? <Check size={13} /> : n}</span>
        <div className="pc-corps">
          <b>{titre}</b>
          {corps}
        </div>
      </li>
    )
  }
  const relanceLe = (() => {
    const d = parseDate(envoi.data?.depuis ?? null)
    return d ? fullDate(new Date(d.getTime() + JOURS_AVANT_RELANCE * 86400_000).toISOString()) : null
  })()

  return (
    <section className="card parcours" aria-label={t('parcours.aria')}>
      <ol>
        {pas(
          1,
          employeur ? t('parcours.cv', { employeur }) : t('parcours.cvSans'),
          <>
            <span className="pc-aide">
              {scoreEstime > score ? t('parcours.cvPretEstime', { estime: scoreEstime, score }) : t('parcours.cvPret')}
            </span>
            {carteCv}
          </>
        )}
        {pas(
          2,
          t('parcours.lettre'),
          etape === 2 ? (
            <span className="pc-act">
              <ActionButton size="sm" variant="primary" onClick={onRedigerLettre}>
                {t('parcours.lettreRediger')}
              </ActionButton>
              <button
                type="button"
                className="linkbtn"
                onClick={() => {
                  try {
                    localStorage.setItem(cleSansLettre(dossierId), '1')
                  } catch {
                    // navigateur sans stockage : l'étape passe pour cette visite
                  }
                  setSansLettre(true)
                }}
              >
                {t('parcours.lettreNon')}
              </button>
            </span>
          ) : null
        )}
        {pas(
          3,
          employeur ? t('parcours.postuler', { employeur }) : t('parcours.postulerSans'),
          etape === 3 ? (
            <span className="pc-act">
              {urlOffre && (
                <ActionButton
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    window.open(urlOffre, '_blank', 'noopener,noreferrer')
                    setPageOuverte(true)
                  }}
                >
                  {t('parcours.ouvrirPage')} <ExternalLink size={13} />
                </ActionButton>
              )}
              <button type="button" className="linkbtn" onClick={() => setPageOuverte(true)}>
                {t('parcours.dejaFait')}
              </button>
            </span>
          ) : null
        )}
        {pas(
          4,
          t('parcours.envoye'),
          etape === 4 ? (
            <span className="pc-act">
              <ActionButton
                size="sm"
                variant="primary"
                onClick={async () => {
                  const id = await creerCandidature()
                  await post(`/api/candidatures/${id}/status`, { status: 'envoye' })
                  toast(t('parcours.envoyeToast'))
                  onChanged()
                }}
              >
                {t('parcours.envoyeOui')}
              </ActionButton>
              <ActionButton
                size="sm"
                onClick={async () => {
                  if (candidature) await post(`/api/candidatures/${candidature.id}/archive`, { archived: true })
                  await post(`/api/offers/${offreId}/status`, { status: 'skip' })
                  toast(t('parcours.envoyeNonToast'))
                  onChanged()
                }}
              >
                {t('parcours.envoyeNon')}
              </ActionButton>
            </span>
          ) : etape === 5 && statut === 'envoye' && envoi.data?.depuis ? (
            <span className="pc-aide">{t('parcours.envoyeLe', { date: fullDate(envoi.data.depuis), relance: relanceLe ?? '' })}</span>
          ) : null
        )}
      </ol>
    </section>
  )
}
